import { useAuthContext } from 'Data/context/Auth';
import { OBJECT_STATUS } from 'Data/Enum';
import VendorData from 'Data/VendorData';
import DataManager from 'Lib/DataManager';
import PropTypes from 'prop-types';
import { createContext, useContext, useEffect, useState } from 'react';
import { getNameWithInactiveTag } from 'Util';
import { getTagValue } from 'Util/tagUtil';

const vendorMap = new Map();
export const getVendorNameFromId = (vendorId) => {
  return (vendorId && vendorMap.get(vendorId)?.displayName) || '';
};

const VendorContext = createContext({
  fullVendorList: [],
  getFilteredList: () => [],
});
VendorContext.displayName = 'VendorContext';

let DM = new DataManager();
export const VendorStateProvider = ({ children }) => {
  const { isLoggedIn } = useAuthContext();

  const [fullVendorList, setFullVendorList] = useState([]);

  useEffect(() => {
    DM.add(
      VendorData.registerListener('ALL_VENDORS_LOADED', onListLoaded),
      VendorData,
    );
    DM.add(
      VendorData.registerListener('RECORD_CREATED', updateMapForVendor),
      VendorData,
    );
    DM.add(
      VendorData.registerListener('RECORD_UPDATED', updateMapForVendor),
      VendorData,
    );
    DM.add(
      VendorData.registerListener('RECORD_ACTIVATED', updateMapForVendor),
      VendorData,
    );
    DM.add(
      VendorData.registerListener('RECORD_DEACTIVATED', updateMapForVendor),
      VendorData,
    );
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      VendorData.populateVendors();
    } else {
      setFullVendorList([]);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (fullVendorList) {
      vendorMap.clear();
      fullVendorList.forEach((vendor) => {
        vendorMap.set(vendor.id, {
          name: vendor.name,
          displayName: vendor.displayName,
          status: vendor.status,
          tags: vendor.tags,
        });
      });
    }
  }, [fullVendorList]);

  const onListLoaded = (list) => {
    setFullVendorList(
      list.map((item) => {
        return { ...item, displayName: getNameWithInactiveTag(item) };
      }),
    );
  };

  const updateMapForVendor = (updatedVendor) => {
    vendorMap.set(updatedVendor.id, {
      name: updatedVendor.name,
      displayName: getNameWithInactiveTag(updatedVendor),
      status: updatedVendor.status,
    });
  };

  const getFilteredList = ({
    showDeactivated = false,
    isForRequest = false,
  }) => {
    return [...vendorMap.entries()]
      .map(([key, value]) => {
        return { id: key, ...value };
      })
      .filter(
        (vendor) =>
          (showDeactivated || vendor.status === OBJECT_STATUS.ACTIVE.key) &&
          (!isForRequest || !getTagValue(vendor, 'hide_for_requests')),
      );
  };

  return (
    <VendorContext.Provider value={{ fullVendorList, getFilteredList }}>
      {children}
    </VendorContext.Provider>
  );
};

VendorStateProvider.propTypes = {
  children: PropTypes.node,
};

export const useVendorContext = () => useContext(VendorContext);
